import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import type { SubjectResult } from '../types';

interface Props {
  data: SubjectResult[];
  title?: string;
}

const COLORS = ['#8b5cf6', '#06b6d4', '#f59e0b', '#22c55e', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];

const SubjectPerformanceChart: React.FC<Props> = ({ data, title = 'Subject-wise Performance' }) => {
  const chartData = data.map((s) => ({
    name: s.subject.length > 18 ? s.subject.substring(0, 16) + '…' : s.subject,
    fullName: s.subject,
    'Perf. Index': s.performanceIndex,
    'Pass %': s.passPercentage,
    appeared: s.totalAppeared,
  }));

  const chartMinWidth = Math.max(900, chartData.length * 95);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload?.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-gray-800 mb-1">{d.fullName}</p>
          <p className="text-purple-600">Perf. Index: <span className="font-bold">{d['Perf. Index']}</span></p>
          <p className="text-green-600">Pass %: <span className="font-bold">{d['Pass %']}%</span></p>
          <p className="text-gray-500">Appeared: {d.appeared}</p>
        </div>
      );
    }
    return null;
  };

  if (!data.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">No data available</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="overflow-x-auto pb-2">
        <div style={{ minWidth: `${chartMinWidth}px` }}>
          <ResponsiveContainer width="100%" height={410}>
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 34 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                interval={0}
                tick={{ fontSize: 11, fill: '#666' }}
                angle={-25}
                textAnchor="end"
                tickMargin={16}
                height={110}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 12, fill: '#666' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" align="right" iconType="circle" />
              <Bar dataKey="Perf. Index" radius={[4, 4, 0, 0]} name="Perf. Index">
                {chartData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default SubjectPerformanceChart;
